import React, { useContext, useEffect, useState } from "react";
import {
  DatePicker,
  Form,
  Input,
  Card,
  Descriptions,
  Button,
  Select,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { HomeOutlined, LineChartOutlined } from "@ant-design/icons";
import { getBranches, getSalesSummary } from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import {
  transactionTypes,
  UserType,
} from "../../../configs/constants";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { CSVLink } from "react-csv";
import { AppContext } from "../../../context/AppContextProvider";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { handleApiError } from "../../../utils/errorHandler";

const SalesSummary = () => {
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [multipaymentTransactions, SetMultipaymentTransactions] = useState([]);
  const [paymentTypesData, setPaymentTypesData] = useState([]);
  const [searchParams, setSearchParams] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const { t } = useTranslation();
  const [searchFormRef] = Form.useForm();
  const { RangePicker } = DatePicker;
  const { authUser } = useContext(AppContext);

  useEffect(() => {
    setIsDataLoading(true);
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchesData(response.data.data.branches);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [t]);

  useEffect(() => {
    setIsDataLoading(true);
    getSalesSummary(searchParams)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setReportData(response.data.data);

        let summary = {};
        summary.gross_sales = response.data.data.transactions_total.total_sales;
        summary.refunds = response.data.data.refunded_transactions_total
          ? response.data.data.refunded_transactions_total
          : 0;
        summary.taxes = response.data.data.transactions_total.taxes;
        summary.net_sales =
          summary.gross_sales -
          Math.abs(summary.refunds) -
          Math.abs(summary.taxes);
        setReportData(summary);

        let data = [];
        transactionTypes.forEach((type) => {
          const transactionType = response.data.data.transactions_by_type.find(
            (transaction) => transaction.type === type.id,
          );

          data.push({
            id: type.id,
            text: type.text,
            translationKey: type.translationKey,
            amount: transactionType ? transactionType.total_charged_amount : 0,
          });
        });

        data.forEach((type, i) => {
          const refundType = response.data.data.refunded_by_type.find(
            (transaction) => transaction.type === type.id,
          );

          if (refundType) {
            data[i].amount =
              data[i].amount - Math.abs(refundType.total_refunded_amount);
          }
        });

        SetMultipaymentTransactions(
          response.data.data.transactions_by_multipayment,
        );

        setPaymentTypesData(data);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [searchParams, t]);

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: null,
      breadcrumbName: t("reports") + " / " + t("sales_summary"),
      icon: <LineChartOutlined />,
    },
  ];

  const boldContentStyle = {
    fontWeight: "bold",
    backgroundColor: "var(--color-bg)",
    textAlign: "right",
  };
  const contentStyle = {
    textAlign: "right",
  };

  const handleSearch = (values) => {
    let search_params = false;
    if (values.dates) {
      let startDateUtc = moment.utc(values.dates[0]).set({
        date: values.dates[0].date() - 1,
        month: values.dates[0].month(),
        year: values.dates[0].year(),
        hour: 19,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      let endDateUtc = moment.utc(values.dates[1]).set({
        date: values.dates[1].date(),
        month: values.dates[1].month(),
        year: values.dates[1].year(),
        hour: 18,
        minute: 59,
        second: 59,
        millisecond: 0,
      });

      search_params = {
        search_start_date: startDateUtc.format(),
        search_end_date: endDateUtc.format(),
      };
    }

    if (values.filter_by_branch) {
      search_params = {
        filter_by_branch: values.filter_by_branch,
        ...search_params,
      };
    }

    if (search_params) {
      setSearchParams(search_params);
    } else {
      setSearchParams([]);
    }
  };
  const handleReset = () => {
    // Reset Form Fields
    searchFormRef.resetFields();
    // Reset Transactions List
    setSearchParams([]);
  };

  const searchForm = (
    <>
      <Form
        name="search-report-form"
        layout="inline"
        onFinish={handleSearch}
        form={searchFormRef}
      >
        <Form.Item name="dates" label={t("date_range")}>
          <RangePicker allowClear format="DD/MM/YYYY" />
        </Form.Item>
        {authUser.type !== UserType.Employee && (
          <Form.Item name="filter_by_branch" label={t("branch")}>
            <Select
              allowClear
              className="app-w-100px"
              options={branchesData.map((branch) => {
                return {
                  label: branch.name,
                  value: branch.name,
                };
              })}
            />
          </Form.Item>
        )}
        <Form.Item>
          <Input
            type="submit"
            value={t("search")}
            className="app-search-submit"
          />
        </Form.Item>
        <Form.Item>
          <Input type="button" value={t("reset")} onClick={handleReset} />
        </Form.Item>
      </Form>
    </>
  );

  const salesCSVData = [
    [
      t("business_name"),
      t("cr_number"),
      authUser.is_vat_exempt ? t("vat_exempt") : t("vat_number"),
      t("gross_sales"),
      t("refunds"),
      t("taxes_on_sale"),
      t("net_sales"),
    ],
    [
      authUser.business_name,
      authUser.cr,
      authUser.is_vat_exempt ? authUser.is_vat_exempt : authUser.vat,
      Number(reportData.gross_sales).toFixed(2),
      Number(reportData.refunds).toFixed(2),
      Number(reportData.taxes * -1).toFixed(2),
      Number(reportData.net_sales).toFixed(2),
    ],
  ];

  const paymentsCSVData = [
    [
      t("cash"),
      t("credit_card"),
      t("multi_payment"),
      t("multi_payment_cash"),
      t("multi_payment_card"),
      t("taxes_on_sale"),
      t("net_total"),
    ],
    [
      paymentTypesData && Number(paymentTypesData[0]?.amount).toFixed(2),
      paymentTypesData && Number(paymentTypesData[1]?.amount).toFixed(2),
      paymentTypesData && Number(paymentTypesData[2]?.amount).toFixed(2),
      multipaymentTransactions &&
        Number(multipaymentTransactions[0]?.total_charged_amount).toFixed(2),
      multipaymentTransactions &&
        Number(multipaymentTransactions[1]?.total_charged_amount).toFixed(2),
      Number(reportData.taxes).toFixed(2),
      paymentTypesData.length &&
        Number(
          paymentTypesData
            .map((item) => item.amount)
            .reduce((prev, next) => prev + next) - Math.abs(reportData.taxes),
        ).toFixed(2),
    ],
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("sales_summary")}
          breadcrumb={CustomBreadcrumb}
          ghost={false}
          footer={searchForm}
          extra={[
            <Button type="primary" key="download-sales-report">
              <CSVLink
                data={salesCSVData}
                filename={
                  t("sales_summary") +
                  " " +
                  moment().format("MM-DD-YYYY HH-mm") +
                  ".csv"
                }
                target="_blank"
              >
                {t("download_sales_report")}
              </CSVLink>
            </Button>,
            <Button type="primary" key="download-payment-report">
              <CSVLink
                data={paymentsCSVData}
                filename={
                  t("payments") +
                  " " +
                  moment().format("MM-DD-YYYY HH-mm") +
                  ".csv"
                }
                target="_blank"
              >
                {t("download_payments_report")}
              </CSVLink>
            </Button>,
          ]}
        />
      </Card>
      <Card loading={isDataLoading} className="app-mb-20">
        <Descriptions
          title={t("sales")}
          bordered={true}
          size="middle"
          span={24}
        >
          <Descriptions.Item
            label={t("gross_sales")}
            span={24}
            labelStyle={{ fontWeight: "bold" }}
            contentStyle={boldContentStyle}
          >
            <CurrencyFormatter value={reportData.gross_sales} />
          </Descriptions.Item>

          <Descriptions.Item
            label={t("refunds")}
            span={24}
            labelStyle={{ background: "none" }}
            contentStyle={contentStyle}
          >
            <CurrencyFormatter value={reportData.refunds} />
          </Descriptions.Item>

          <Descriptions.Item
            label={t("taxes_on_sale")}
            span={24}
            labelStyle={{ background: "none" }}
            contentStyle={contentStyle}
          >
            <CurrencyFormatter value={reportData.taxes * -1} />
          </Descriptions.Item>

          <Descriptions.Item
            label={t("net_sales")}
            span={24}
            labelStyle={{ fontWeight: "bold" }}
            contentStyle={boldContentStyle}
          >
            <CurrencyFormatter value={reportData.net_sales} />
          </Descriptions.Item>

          {/* <Descriptions.Item
            label={t("total_sales")}
            span={24}
            labelStyle={{ fontWeight: "bold" }}
            contentStyle={boldContentStyle}
          >
            <CurrencyFormatter value={reportData.total_sales} />
          </Descriptions.Item> */}
        </Descriptions>
      </Card>
      <Card loading={isDataLoading}>
        <Descriptions
          title={t("payments")}
          bordered={true}
          size="middle"
          span={24}
        >
          {paymentTypesData.map((type) => (
            <Descriptions.Item
              key={type.id}
              label={t(type.translationKey)}
              span={24}
              labelStyle={{ background: "none" }}
              contentStyle={contentStyle}
            >
              <CurrencyFormatter value={type.amount} />
            </Descriptions.Item>
          ))}
          <Descriptions.Item
            label={t("multi_payment_cash")}
            span={24}
            labelStyle={{
              fontWeight: "bold",
              width: "60%",
              background: "none",
            }}
            contentStyle={contentStyle}
          >
            {multipaymentTransactions && (
              <CurrencyFormatter
                value={multipaymentTransactions[0]?.total_charged_amount ?? 0}
              />
            )}
          </Descriptions.Item>

          <Descriptions.Item
            label={t("multi_payment_card")}
            span={24}
            labelStyle={{
              fontWeight: "bold",
              width: "60%",
              background: "none",
            }}
            contentStyle={contentStyle}
          >
            {multipaymentTransactions && (
              <CurrencyFormatter
                value={multipaymentTransactions[1]?.total_charged_amount ?? 0}
              />
            )}
          </Descriptions.Item>

          <Descriptions.Item
            label={t("taxes_on_sale")}
            span={24}
            labelStyle={{ fontWeight: "bold", width: "60%" }}
            contentStyle={boldContentStyle}
          >
            <CurrencyFormatter value={reportData.taxes} />
          </Descriptions.Item>

          <Descriptions.Item
            label={t("net_total")}
            span={24}
            labelStyle={{ fontWeight: "bold", width: "60%" }}
            contentStyle={boldContentStyle}
          >
            {paymentTypesData.length && (
              <CurrencyFormatter
                value={
                  paymentTypesData
                    .map((item) => item.amount)
                    .reduce((prev, next) => prev + next) -
                  Math.abs(reportData.taxes)
                }
              />
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </>
  );
};

export default SalesSummary;
